<template v-if="documentConfig">
    <hr />

    <DsCheckbox
        v-model="needsEsignature"
        label="Request eSignature(s)"
        name="needsEsignature"
        value="1"
        @update:modelValue="fileDocumentStore.clearDocumentErrorByKeys(documentId, [DOCUMENT_SIGN_GENERAL, DOCUMENT])"
    />

    <template v-if="needsEsignature">
        <div class="flex flex-col gap-3 text-content-primary">
            <DsTooltip
                class="flex-1"
                :tooltipText="!clientOptions.length ? 'Select at least one client first' : ''"
                tooltipLocation="bottom"
            >
                <DsMultiSelect
                    v-model="selectedSigners"
                    :id="`signers-${instanceId}`"
                    class="flex-1"
                    :disabled="clientOptions.length === 0"
                    :error="documentErrors.get(ALL_SIGNERS_ERROR)"
                    label="Signer(s)"
                    :options="clientOptions"
                    placeholder="Select signer(s)"
                    required
                    @update:modelValue="fileDocumentStore.clearDocumentErrorByKeys(documentId, [DOCUMENT_SIGNERS])"
                >
                    <template #optionLabel="{ option }">
                        <ClientMultiSelectOption
                            :email="option.email"
                            :first="option.first"
                            :last="option.last"
                            :role="option.role"
                        />
                    </template>
                    <template #chipContent="{ option, truncate }">
                        <span>
                            {{ truncate(getClientChipContent(option)) }}
                            <span class="text-content-secondary">(Signer {{ signerNumberById.get(option.id) }})</span>
                        </span>
                    </template>
                </DsMultiSelect>
            </DsTooltip>
            <div class="w-1/2">
                <DsButton
                    :disabled="fileDocumentStore.isPreparingESign || selectedSigners.length === 0"
                    leading="fa-regular fa-pen"
                    size="sm"
                    :title="!selectedSigners.length ? 'Select at least one signer to review/edit eSign document' : ''"
                    variant="tertiary"
                    @click="startReviewEditEsign"
                >
                    <span v-if="fileDocumentStore.isPreparingESign">Preparing document...</span>
                    <span v-else>Review/edit eSign document</span>
                </DsButton>
            </div>
        </div>
    </template>
</template>

<script setup lang="ts">
import DsButton from '@/components/design-system/form/input/DsButton.vue';
import DsCheckbox from '@/components/design-system/form/input/DsCheckbox.vue';
import DsMultiSelect from '@/components/design-system/multiselect/DsMultiSelect.vue';
import DsTooltip from '@/components/design-system/tooltip/DsTooltip.vue';
import ClientMultiSelectOption from '@/components/Views/Company/File/ClientMultiSelectOption.vue';
import { getClientChipContent } from '@/components/Views/Company/File/Documents/document-upload-utils';
import { useDocumentEsignSetup } from '@/components/Views/Company/File/Documents/useDocumentEsignSetup';
import type { DocumentActionRoutes, DocumentUploadConfig, File } from '@/components/Views/Company/File/file.types';
import {
    DocumentUploadMergedErrorKeys,
    DocumentUploadValidationErrorKeys,
    type ClientOption,
} from '@/components/Views/Company/File/file.types';
import { ESIGN_INVALIDATION_DIALOG_INJECTION_KEY } from '@/injection-keys';
import { getSetDifference } from '@/shared/utils/set-difference';
import { useFileDocumentStore } from '@/stores/file-document.store';
import { computed, inject, useId, watch } from 'vue';

const { DOCUMENT, DOCUMENT_SIGN_GENERAL, DOCUMENT_SIGNERS } = DocumentUploadValidationErrorKeys;
const { ALL_SIGNERS_ERROR } = DocumentUploadMergedErrorKeys;

const ESIGN_INVALIDATED_ERROR =
    'Client details for this document have been edited. Use the "Review/edit eSign" button above to update the eSignature document.';

const instanceId = useId();
const props = defineProps<{
    documentId: DocumentUploadConfig['documentId'];
    clientOptions: ClientOption[];
    branchId: File['branch_id'];
    routes: DocumentActionRoutes;
}>();

const fileDocumentStore = useFileDocumentStore();

const documentConfig = computed(() => fileDocumentStore.documentConfigs.get(props.documentId)!);
const documentErrors = computed(() => documentConfig?.value?.mergedErrors ?? new Map());

const selectedSigners = defineModel<ClientOption[]>('selectedSigners', { default: [] });
const needsEsignature = defineModel<boolean>('needsEsignature');

// Provided by the documents section in the add-file form. Outside that context (e.g. the file
// detail upload sheet) there is no dialog to raise, so default to a no-op.
const { openEsignInvalidationDialog } = inject(ESIGN_INVALIDATION_DIALOG_INJECTION_KEY, {
    openEsignInvalidationDialog: () => {},
    closeEsignInvalidationDialog: () => {},
});

/**
 * Maps each selected signer's id to its 1-indexed position. The selection order is what the
 * backend maps to DocuSign signer fields (Signer 1, Signer 2, …), so this lets each chip show
 * which signer it represents. See CR-512.
 */
const signerNumberById = computed(() => {
    const map = new Map<ClientOption['id'], number>();
    selectedSigners.value.forEach((signer, index) => map.set(signer.id, index + 1));
    return map;
});

const { initiateEsignEditTab, esignEditId, esignEditUrl, setupError, errorResponse } = useDocumentEsignSetup({
    selectedSigners,
    branchId: props.branchId,
    documentConfig,
    routes: props.routes,
});

const updateConfig = () => {
    fileDocumentStore.updateDocument(props.documentId, {
        signers: selectedSigners.value.map(r => Number(r.id)),
        needsEsignature: needsEsignature.value,
        esignEditId: esignEditId.value,
        esignEditUrl: esignEditUrl.value,
    });
};

const startReviewEditEsign = async () => {
    const { DOCUMENT, DOCUMENT_SIGNERS, DOCUMENT_SIGN_GENERAL } = DocumentUploadValidationErrorKeys;
    fileDocumentStore.updateEsignStateInvalidationError(documentConfig.value.documentId, '');
    fileDocumentStore.clearDocumentErrorByKeys(documentConfig.value.documentId, [
        DOCUMENT,
        DOCUMENT_SIGNERS,
        DOCUMENT_SIGN_GENERAL,
    ]);
    fileDocumentStore.updateFirstLevelError();

    await initiateEsignEditTab();

    if (setupError.value) {
        fileDocumentStore.updateDocumentErrors(props.documentId, {
            documents_sign: [setupError.value],
        });
    }

    if (errorResponse.value) {
        // first level error can contain relevant context or downtime information
        // all errors refer to the same document
        fileDocumentStore.updateOperationErrorForDocument(props.documentId, errorResponse.value);
    }

    if (esignEditId.value && esignEditUrl.value) {
        updateConfig();
    }
};

// When a signer's email is edited upstream the prepared eSign document becomes stale, so we
// watch the source client list (not selectedSigners — manual signer changes are handled
// below) and invalidate when a currently selected signer's email actually changed.
watch(
    () => props.clientOptions,
    (newOptions, oldOptions) => {
        if (!documentConfig.value?.esignEditId) return;

        const selectedSignerIds = new Set(selectedSigners.value.map(signer => signer.id));
        const signerEmails = (options: ClientOption[]) =>
            new Set(options.filter(option => selectedSignerIds.has(option.id)).map(option => option.email));

        const { added, removed } = getSetDifference(signerEmails(oldOptions), signerEmails(newOptions));
        if (added.size === 0 && removed.size === 0) return;

        // A selected signer's email changed, so the prepared eSign document is stale. Keep the
        // user's selection intact and invalidate only the prepared edit so they can redo it, then
        // surface the invalidation error and raise the shared dialog explaining the next step.
        // The error lives on its own config field, so it is unaffected by the merged-error map
        // rebuilds that the recipient/signer watches trigger off this same change.
        esignEditId.value = undefined;
        esignEditUrl.value = undefined;
        fileDocumentStore.updateDocument(props.documentId, {
            esignEditId: undefined,
            esignEditUrl: undefined,
        });
        fileDocumentStore.updateEsignStateInvalidationError(documentConfig.value.documentId, ESIGN_INVALIDATED_ERROR);
        openEsignInvalidationDialog();
    },
);

watch(selectedSigners, (newSigners, oldSigners) => {
    const oldSequence = oldSigners.map(s => s.id).join(',');
    const newSequence = newSigners.map(s => s.id).join(',');
    const sequenceChanged = oldSequence !== newSequence;

    if (sequenceChanged) {
        fileDocumentStore.updateEsignStateInvalidationError(documentConfig.value.documentId, '');
        fileDocumentStore.clearDocumentErrorByKeys(props.documentId, [DOCUMENT_SIGNERS]);
    }
    if (documentConfig.value?.esignEditId && sequenceChanged) {
        esignEditId.value = undefined;
        esignEditUrl.value = undefined;
        // Signers changed - clear eSign setup to force new creation
        fileDocumentStore.updateDocument(props.documentId, {
            needsEsignature: needsEsignature.value,
            signers: newSigners.map(s => Number(s.value)),
            esignEditId: undefined,
            esignEditUrl: undefined,
        });

        return;
    }
    updateConfig();
});

watch(needsEsignature, newNeedsEsignature => {
    if (!newNeedsEsignature) {
        fileDocumentStore.updateEsignStateInvalidationError(documentConfig.value.documentId, '');
        fileDocumentStore.updateFirstLevelError();
        selectedSigners.value = [];
        // Clear eSign setup when unchecking
        if (documentConfig.value?.esignEditId) {
            fileDocumentStore.updateDocument(props.documentId, {
                needsEsignature: newNeedsEsignature,
                signers: [],
                esignEditId: undefined,
                esignEditUrl: undefined,
            });
            return;
        }
    }

    updateConfig();
});

watch(
    () => props.clientOptions,
    () => {
        // If selected signers are no longer in client options, remove them
        const validSignerIds = new Set(props.clientOptions.map(c => c.id));
        selectedSigners.value = selectedSigners.value.filter(signer => validSignerIds.has(signer.id));
    },
);
</script>
